import {
  Box,
  Paper,
  Typography,
  InputAdornment,
  IconButton,
  TextField,
  CircularProgress,
} from '@mui/material'
import { Button } from '@ds/components'
import { useState } from 'react'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { type Control, type FieldErrors, Controller } from 'react-hook-form'
import { PasswordRequirements } from '../PasswordRequirements'
import { RESET_PASSWORD_FORM_STRINGS } from './strings'

type TResetPasswordFormData = {
  password: string
  confirmPassword: string
}

type TPasswordValidation = {
  length: boolean
  uppercase: boolean
  lowercase: boolean
  digit: boolean
}

type TResetPasswordFormProps = {
  control: Control<TResetPasswordFormData>
  errors: FieldErrors<TResetPasswordFormData>
  isValid: boolean
  isLoading: boolean
  passwordValidation: TPasswordValidation
  watchedPassword: string
  onBackToLogin: () => void
  onSubmit: (e: React.FormEvent) => void
}

export const ResetPasswordForm = ({
  control,
  errors,
  isValid,
  isLoading,
  passwordValidation,
  watchedPassword,
  onBackToLogin,
  onSubmit,
}: TResetPasswordFormProps) => {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        pt: { xs: 12, sm: 8, md: 6 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4, md: 5 },
          minWidth: { xs: '90%', sm: 400, md: 480 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Typography variant="h4" sx={{ mb: 4, fontWeight: 500 }}>
          {RESET_PASSWORD_FORM_STRINGS.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          {RESET_PASSWORD_FORM_STRINGS.description}
        </Typography>

        <Box component="form" sx={{ width: '100%' }} onSubmit={onSubmit}>
          <Controller
            name="password"
            control={control}
            rules={{
              required: RESET_PASSWORD_FORM_STRINGS.passwordRequired,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label={RESET_PASSWORD_FORM_STRINGS.passwordLabel}
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                sx={{
                  mb: 2,
                  '& .MuiFormHelperText-root': {
                    minHeight: '20px',
                    margin: '4px 0 0 0',
                  },
                }}
                autoComplete="new-password"
                error={!!errors.password}
                helperText={errors.password?.message || ' '}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(show => !show)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />

          <PasswordRequirements validation={passwordValidation} />

          <Controller
            name="confirmPassword"
            control={control}
            rules={{
              required: RESET_PASSWORD_FORM_STRINGS.confirmPasswordRequired,
              validate: value => {
                if (value !== watchedPassword) {
                  return RESET_PASSWORD_FORM_STRINGS.passwordsDoNotMatch
                }
                return true
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label={RESET_PASSWORD_FORM_STRINGS.confirmPasswordLabel}
                variant="outlined"
                type={showConfirmPassword ? 'text' : 'password'}
                fullWidth
                sx={{
                  mb: 3,
                  '& .MuiFormHelperText-root': {
                    minHeight: '20px',
                    margin: '4px 0 0 0',
                  },
                }}
                autoComplete="new-password"
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message || ' '}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(show => !show)}
                          edge="end"
                          size="small"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />

          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ py: 1.5 }}
              disabled={isLoading || !isValid}
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {isLoading
                ? RESET_PASSWORD_FORM_STRINGS.updatingPasswordButton
                : RESET_PASSWORD_FORM_STRINGS.updatePasswordButton}
            </Button>

            <Button
              variant="text"
              color="primary"
              onClick={onBackToLogin}
              disabled={isLoading}
              sx={{ color: 'text.primary' }}
            >
              {RESET_PASSWORD_FORM_STRINGS.backToLoginButton}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}
